import {
    GetAccountsResponse,
    GetApplicationResponse,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';

import { isOfferTimedOut } from './MyOffers.helpers';

export type TOfferStatus =
    | 'offered'
    | 'offer_expired'
    | 'application_offer_revoked'
    | 'application_declined'
    | 'application_pending'
    | 'application_deferred'
    | 'no_offer';

export const getOfferStatus = (
    getFormalOffersResponse: GetFormalOffersResponse,
    getApplicationResponse: GetApplicationResponse,
    getAccountsResponse: GetAccountsResponse
): TOfferStatus => {
    if (
        getApplicationResponse.data === null &&
        getFormalOffersResponse.data?.formal_offers.length === 0 &&
        getAccountsResponse.data?.accounts.length === 0
    ) {
        return 'no_offer';
    }
    if (getApplicationResponse.data) {
        //if customer has no valid formal offer or no offer for their requested product,
        //and application response status is open, declined or deferred
        //=> return respective views
        const hasSuggestedProductFormalOffer =
            hasValidFormalOfferforSuggestedProduct(
                getApplicationResponse,
                getFormalOffersResponse
            );

        //A customer could have a micro approval, but pursuing manual decision. We would not want to show them their offer at this stage.
        // When True, we should show the approval page, when False, we should show the pending page.
        if (!getApplicationResponse.data.is_closed) {
            return 'application_pending';
        }

        if (!hasSuggestedProductFormalOffer) {
            // @ts-expect-error // lapi client types are wrong
            if (getApplicationResponse.data!.status === 'offer_revoked') {
                return 'application_offer_revoked';
            } else if (getApplicationResponse.data!.status === 'open') {
                return 'application_pending';
            } else if (getApplicationResponse.data!.status === 'declined') {
                return 'application_declined';
            } else if (getApplicationResponse.data!.status === 'deferred') {
                //a customer could have deferred applications with formal offers still valid, we want them to see their valid offer
                if (!hasValidFormalOffer(getFormalOffersResponse)) {
                    return 'application_deferred';
                }
            }
        }
    }
    if (getFormalOffersResponse.data) {
        // if the only formal offers are for iwocapay, show no offers, as they are handled by the iwocapay page
        if (hasOnlyIwocapayFormalOffers(getFormalOffersResponse)) {
            return 'no_offer';
        }

        //introducer customers can have an offered application but no selected formal offers
        if (
            getApplicationResponse.data!.status === 'offered' &&
            getFormalOffersResponse.data.formal_offers.length === 0
        ) {
            return 'application_pending';
        }
        //check there are no active accounts for the product- an active account could mean a topup formal offer

        for (const formalOffer of getFormalOffersResponse.data.formal_offers) {
            if (
                hasActiveAccounts(
                    getAccountsResponse.data!.accounts,
                    formalOffer.product_type!
                )
            ) {
                return 'no_offer';
            }
        }

        if (isOfferTimedOut(getFormalOffersResponse.data!.formal_offers)) {
            return 'offer_expired';
        } else return 'offered';
        //TODO: handle invalid/revoked offers
    }
    return 'no_offer';
};

const hasActiveAccounts = (
    accounts: NonNullable<GetAccountsResponse['data']>['accounts'],
    formalOfferProductType: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][0]['product_type']
) => {
    return accounts.some((account) => {
        if (
            !account.account_id &&
            account.product_type === formalOfferProductType
        )
            return false;

        return (
            account.loan_status !== 'ended' &&
            account.product_type === formalOfferProductType
        );
    });
};

const hasValidFormalOfferforSuggestedProduct = (
    getApplicationResponse: GetApplicationResponse,
    getFormalOffersResponse: GetFormalOffersResponse
): boolean => {
    const suggested_product =
        getApplicationResponse.data?.requested_products![0].suggested_product!;
    if (
        !getFormalOffersResponse.data ||
        getFormalOffersResponse.data.formal_offers.length === 0
    ) {
        return false;
    } else if (
        hasValidFormalOffer(getFormalOffersResponse) &&
        suggested_product.product_type ===
            getFormalOffersResponse.data?.formal_offers[0]?.product_type
    )
        return true;
    else {
        return false;
    }
};

const hasValidFormalOffer = (
    getFormalOffersResponse: GetFormalOffersResponse
): boolean => {
    if (
        !getFormalOffersResponse.data ||
        getFormalOffersResponse.data.formal_offers.length === 0
    ) {
        return false;
    } else {
        return getFormalOffersResponse.data?.formal_offers[0].is_valid!;
    }
};

const hasOnlyIwocapayFormalOffers = (
    getFormalOffersResponse: GetFormalOffersResponse
): boolean => {
    const formal_offers = getFormalOffersResponse.data?.formal_offers;
    if (!formal_offers || formal_offers.length === 0) return false;
    return formal_offers.every(
        (formal_offers) => formal_offers.product_type === 'iwocapay'
    );
};
